import { useState } from 'react';
import { useAuth } from '@/lib/contexts/auth-context';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldLabel, FieldDescription } from '@/components/ui/field';
import { Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const FORM_SUBSIDIARY_CREATE_ID = 'form-subsidiary-create';

export default function FormSubsidiaryCreate({ onSubmit }) {
    const { t } = useTranslation();
    const { user } = useAuth();

    const [formData, setFormData] = useState({
        name: '',
        siret: '',
        vat: '',
        description: '',
        notes: '',
        street: '',
        postalCode: '',
        city: '',
        phone: '',
        email: '',
        ...(user.role === 'admin' && { billingId: '' }),
        managerFirstName: '',
        managerLastName: '',
        managerEmail: '',
        allowTokenPurchase: true,
    });

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (
            !formData.name.trim() ||
            !formData.siret.trim() ||
            !formData.email.trim() ||
            !formData.street.trim() ||
            !formData.postalCode.trim() ||
            !formData.city.trim()
        )
            return;
        onSubmit(formData);
    };

    return (
        <form id={FORM_SUBSIDIARY_CREATE_ID} onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5">
            <div className="space-y-5">
                {/* Nom de l'entreprise */}
                <Field>
                    <FieldLabel required>{t('account:nomDeLEntreprise')}</FieldLabel>
                    <Input
                        type="text"
                        placeholder={t('account:exMonSousCompteSas')}
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                    />
                </Field>

                {/* SIRET */}
                <Field>
                    <FieldLabel required>SIRET</FieldLabel>
                    <Input
                        type="text"
                        inputMode="numeric"
                        placeholder={t('account:ex12345678900012')}
                        maxLength={14}
                        value={formData.siret}
                        onChange={(e) => handleChange('siret', e.target.value.replace(/\D/g, '').slice(0, 14))}
                        required
                    />
                </Field>

                {/* N° TVA intracommunautaire */}
                <Field>
                    <FieldLabel>{t('account:nTvaIntracommunautaire')}</FieldLabel>
                    <Input
                        type="text"
                        placeholder={t('account:fr12345678901')}
                        maxLength={20}
                        value={formData.vat}
                        onChange={(e) => handleChange('vat', e.target.value)}
                    />
                </Field>

                {/* Description */}
                <Field>
                    <FieldLabel>{t('account:description')}</FieldLabel>
                    <Textarea
                        placeholder={t('account:ajoutezUneDescription')}
                        rows={3}
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                    />
                </Field>

                {/* Notes */}
                <Field>
                    <FieldLabel>{t('account:notes')}</FieldLabel>
                    <Textarea
                        placeholder={t('account:ajoutezDesNotes')}
                        rows={3}
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                    />
                </Field>

                {/* Adresse */}
                <Field>
                    <FieldLabel required>{t('account:adresse')}</FieldLabel>
                    <Input
                        type="text"
                        placeholder={t('account:ex15AvenueDeLaRepublique')}
                        value={formData.street}
                        onChange={(e) => handleChange('street', e.target.value)}
                        required
                    />
                </Field>

                {/* Code postal + Ville */}
                <div className="flex gap-2">
                    <Field className="flex-1">
                        <FieldLabel required>{t('account:codePostal')}</FieldLabel>
                        <Input
                            type="text"
                            placeholder={t('account:ex69003')}
                            value={formData.postalCode}
                            onChange={(e) => handleChange('postalCode', e.target.value)}
                            required
                        />
                    </Field>
                    <Field className="flex-[2]">
                        <FieldLabel required>{t('account:ville')}</FieldLabel>
                        <Input
                            type="text"
                            placeholder={t('account:exLyon')}
                            value={formData.city}
                            onChange={(e) => handleChange('city', e.target.value)}
                            required
                        />
                    </Field>
                </div>

                {/* Téléphone */}
                <Field>
                    <FieldLabel>{t('account:telephone')}</FieldLabel>
                    <Input
                        type="tel"
                        placeholder={t('account:ex33472000000')}
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                    />
                </Field>

                {/* Email de contact */}
                <Field>
                    <FieldLabel required>{t('account:adresseDeContact')}</FieldLabel>
                    <Input
                        type="email"
                        placeholder={t('account:exContactSousCompteFr')}
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                    />
                </Field>

                {/* ID de facturation */}
                {
                    user.role === 'admin' && (
                        <Field>
                            <FieldLabel>
                                {t('account:identifiantDeFacturation')}
                            </FieldLabel>
                            <Input
                                type="text"
                                placeholder={t('account:ex123')}
                                value={formData.billingId}
                                onChange={(e) => handleChange('billingId', e.target.value)}
                            />
                        </Field>
                    )
                }

                {/* Gérant section */}
                <div className="border-t border-border pt-5">
                    <h3 className="text-[15px] text-foreground mb-3">{t('account:gerantFacultatif')}</h3>
                    <p className="text-[13px] text-muted-foreground mb-4">
                        {t('account:unEmailDInvitationSeraEnvoyeAuGerant')}
                    </p>

                    <div className="space-y-4">
                        <div className="flex gap-2">
                            <Field className="flex-1">
                                <FieldLabel>{t('account:prenom')}</FieldLabel>
                                <Input
                                    type="text"
                                    placeholder={t('account:exJean')}
                                    value={formData.managerFirstName}
                                    onChange={(e) => handleChange('managerFirstName', e.target.value)}
                                />
                            </Field>
                            <Field className="flex-1">
                                <FieldLabel>{t('account:nom2')}</FieldLabel>
                                <Input
                                    type="text"
                                    placeholder={t('account:exDupont')}
                                    value={formData.managerLastName}
                                    onChange={(e) => handleChange('managerLastName', e.target.value)}
                                />
                            </Field>
                        </div>

                        <Field>
                            <FieldLabel>{t('account:adresseEmail')}</FieldLabel>
                            <Input
                                type="email"
                                placeholder={t('account:exJeanDupontSousCompteFr')}
                                value={formData.managerEmail}
                                onChange={(e) => handleChange('managerEmail', e.target.value)}
                            />
                        </Field>
                    </div>
                </div>

                {/* Token purchase permission */}
                <div className="border-t border-border pt-5">
                    <Field orientation="horizontal" className="justify-between">
                        <div>
                            <h3 className="text-[15px] text-foreground mb-1">{t('account:autoriserLAchatDeJetons')}</h3>
                            <FieldDescription className="text-[13px] text-muted-foreground">
                                {t('account:permettreACetteEntrepriseDAcheterDesJetons')}
                            </FieldDescription>
                        </div>
                        <Switch
                            checked={formData.allowTokenPurchase}
                            onCheckedChange={(checked) => handleChange('allowTokenPurchase', checked)}
                        />
                    </Field>
                </div>
            </div>
        </form>
    );
}
